import React, { useEffect, useMemo, useState } from "react";

const pad2 = (n) => String(n).padStart(2, "0");

// ... existing code ...

export default function AnalogClock({
                                        size = 160,
  showDigital = true,
  className = "",
  style = {},
  variant = "light", // "light" | "dark"
}) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const { hourDeg, minuteDeg, secondDeg, digital } = useMemo(() => {
    const d = now;
    const ms = d.getMilliseconds();
    const s = d.getSeconds() + ms / 1000;
    const m = d.getMinutes() + s / 60;
    const h = (d.getHours() % 12) + m / 60;

    const hours24 = d.getHours();
    const hours12 = ((hours24 + 11) % 12) + 1; // 1..12
    const ampm = hours24 >= 12 ? "PM" : "AM";

    return {
      hourDeg: h * 30, // 360/12
      minuteDeg: m * 6, // 360/60
      secondDeg: s * 6, // 360/60
      digital: `${pad2(hours12)}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())} ${ampm}`,
    };
  }, [now]);

  const faceSize = size;
  const center = faceSize / 2;

  const palette =
    variant === "dark"
      ? {
          faceBg: "radial-gradient(circle at 30% 30%, #3a414c 0%, #2b3038 55%, #23272e 100%)",
          border: "1px solid rgba(255,255,255,0.14)",
          shadow: "0 14px 34px rgba(0,0,0,0.45)",
          tickHour: "rgba(255,255,255,0.72)",
          tickMin: "rgba(255,255,255,0.32)",
          hourHand: "rgba(255,255,255,0.85)",
          minuteHand: "rgba(255,255,255,0.65)",
          secondHand: "#22c55e",
          cap: "rgba(255,255,255,0.85)",
          digital: "rgba(255,255,255,0.82)",
        }
      : {
          faceBg: "radial-gradient(circle at 30% 30%, #ffffff 0%, #f5f7ff 45%, #eef2ff 100%)",
          border: "2px solid rgba(15, 23, 42, 0.12)",
          shadow: "0 8px 24px rgba(15, 23, 42, 0.10)",
          tickHour: "rgba(15,23,42,0.55)",
          tickMin: "rgba(15,23,42,0.25)",
          hourHand: "rgba(15,23,42,0.75)",
          minuteHand: "rgba(15,23,42,0.55)",
          secondHand: "#ef4444",
          cap: "rgba(15,23,42,0.85)",
          digital: "rgba(15,23,42,0.75)",
        };

  return (
    <div className={className} style={{ display: "inline-flex", flexDirection: "column", gap: 8, ...style }}>
      <div
        style={{
          width: faceSize,
          height: faceSize,
          borderRadius: "50%",
          background: palette.faceBg,
          border: palette.border,
          boxShadow: palette.shadow,
          position: "relative",
        }}
        aria-label="Analog clock"
        role="img"
      >
        {/* Ticks */}
        {Array.from({ length: 60 }).map((_, i) => {
          const isHour = i % 5 === 0;
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                left: center,
                top: center,
                width: isHour ? 6 : 3,
                height: isHour ? 14 : 8,
                background: isHour ? palette.tickHour : palette.tickMin,
                borderRadius: 999,
                transform: `rotate(${i * 6}deg) translateY(-${center - 12}px)`,
                transformOrigin: "center center",
              }}
            />
          );
        })}

        {/* Hands */}
        <div
          style={{
            position: "absolute",
            left: center,
            top: center,
            width: 6,
            height: center * 0.55,
            background: palette.hourHand,
            borderRadius: 999,
            transform: `translate(-50%, -100%) rotate(${hourDeg}deg)`,
            transformOrigin: "50% 100%",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: center,
            top: center,
            width: 4,
            height: center * 0.75,
            background: palette.minuteHand,
            borderRadius: 999,
            transform: `translate(-50%, -100%) rotate(${minuteDeg}deg)`,
            transformOrigin: "50% 100%",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: center,
            top: center,
            width: 2,
            height: center * 0.82,
            background: palette.secondHand,
            borderRadius: 999,
            transform: `translate(-50%, -100%) rotate(${secondDeg}deg)`,
            transformOrigin: "50% 100%",
          }}
        />

        {/* Center cap */}
        <div
          style={{
            position: "absolute",
            left: center,
            top: center,
            width: 12,
            height: 12,
            borderRadius: "50%",
            background: palette.cap,
            transform: "translate(-50%, -50%)",
            boxShadow: "0 2px 10px rgba(0,0,0,0.35)",
          }}
        />
      </div>

      {showDigital && (
        <div
          style={{
            fontFamily:
              "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
            fontSize: 14,
            color: palette.digital,
            textAlign: "center",
          }}
        >
          {digital}
        </div>
      )}
    </div>
  );
}